import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { Skeleton } from "../../../../../components/ui";
import { RmStatusBody } from "../../../../../components/client/RmStatusBody";
import {
  buildProgramTimeline,
  listRecentSubmissions,
  listTopCandidates,
  type ProgramTimelinePoint,
  type RecentCandidateRow,
  type RecentSubmissionRow,
} from "../../../../../lib/clientDashboard";
import { listMyCompetitions } from "../../../../../lib/clientCompetitions";
import { useRequireApprovedCase } from "../../../../../lib/useRequireApprovedCase";
import { formatDeadlineCompact } from "../../../../../lib/datetime";
import { resolveActiveCaseId, writeActiveCaseId } from "../../../../../lib/useActiveCase";
import type { ClientCompetitionListRow } from "../../../../../types/database";

const THEME = PANE_THEMES.Client;
const BLACK = THEME.pageBg;
const OFF_BLACK = THEME.surfaceBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const AMBER_WARM = COLORS.AMBER_WARM.hex;
const LINEN = THEME.textOnSurface;
const GREEN = THEME.success;
const RED = THEME.destructive;

const W = "rgba(255,255,255,1)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const WD = "rgba(255,255,255,0.10)";
const BORDER = "rgba(255,255,255,0.07)";
const BORDER_A = "rgba(181,135,58,0.22)";

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const mins = Math.max(1, Math.round((Date.now() - t) / 60_000));
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatBytes(b: number | null): string {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fileEmojiFor(name: string): string {
  const ext = name.toLowerCase().split(".").pop() || "";
  if (ext === "pdf") return "📄";
  if (ext === "xlsx" || ext === "xls" || ext === "csv") return "📊";
  if (ext === "pptx" || ext === "ppt" || ext === "key") return "📙";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "ipynb") return "🧪";
  return "📁";
}

function caseStatusLabel(s: string, days: number | null): string {
  if (s === "open" || s === "judging") {
    if (days != null) {
      return `Program active · ${days} day${days === 1 ? "" : "s"} remaining`;
    }
    return s === "judging" ? "Judging in progress" : "Program active";
  }
  if (s === "draft") return "Draft — not yet published";
  if (s === "closed") return "Closed";
  return s;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.ceil((t - Date.now()) / 86_400_000));
}

export default function ClientDashboardScreen() {
  // Route guard — pre-approval cases (draft / denied / submitted / etc.)
  // get bounced to /edit because there's no live activity to show here.
  // The hook fires a router.replace during its useEffect, so the page
  // briefly mounts then unmounts. That's fine — we just don't fight it.
  useRequireApprovedCase();
  const [rows, setRows] = useState<ClientCompetitionListRow[]>([]);
  const params = useLocalSearchParams<{ id: string }>();
  const urlCaseId = typeof params.id === 'string' ? params.id : Array.isArray(params.id) ? params.id[0] : null;
  const [activeCaseId, setActiveCaseIdState] = useState<string | null>(urlCaseId);

  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<RecentCandidateRow[]>([]);
  const [submissions, setSubmissions] = useState<RecentSubmissionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const list = await listMyCompetitions();
      setRows(list);
      // Trust the URL param as the source of truth. Persist it to
      // sessionStorage so the switcher across other pages stays aligned.
      const fromUrl = urlCaseId && list.some((r) => r.id === urlCaseId) ? urlCaseId : null;
      const resolved = fromUrl ?? resolveActiveCaseId(list);
      if (resolved) writeActiveCaseId(resolved);
      setActiveCaseIdState(resolved);
      if (resolved) {
        const [cands, subs] = await Promise.all([
          listTopCandidates(resolved, 4),
          listRecentSubmissions(resolved, 4),
        ]);
        setCandidates(cands);
        setSubmissions(subs);
      } else {
        setCandidates([]);
        setSubmissions([]);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load dashboard");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // If the URL's caseId doesn't match a case we own, redirect to /client and
  // let it pick a default. Most commonly hit when someone bookmarks a case
  // that's since been deleted or transferred.
  useEffect(() => {
    if (loading) return;
    if (urlCaseId && !rows.some((r) => r.id === urlCaseId)) {
      router.replace("/client" as Href);
    }
  }, [loading, urlCaseId, rows]);

  const focus = useMemo(
    () => (activeCaseId ? rows.find((r) => r.id === activeCaseId) ?? null : null),
    [activeCaseId, rows]
  );

  const totals = useMemo(() => {
    const reg = rows.reduce((s, r) => s + (r.registration_count ?? 0), 0);
    const teams = rows.reduce((s, r) => s + (r.team_count ?? 0), 0);
    const subs = rows.reduce((s, r) => s + (r.submission_count ?? 0), 0);
    return { reg, teams, subs };
  }, [rows]);

  const days = useMemo(() => daysUntil(focus?.closes_at ?? null), [focus?.closes_at]);

  const focusCount = useMemo(
    () => ({
      registered: focus?.registration_count ?? 0,
      teams: focus?.team_count ?? 0,
      submitted: focus?.submission_count ?? 0,
    }),
    [focus]
  );

  const timeline: ProgramTimelinePoint[] = useMemo(() => {
    if (!focus) return [];
    return buildProgramTimeline({
      registration_open_at: (focus as unknown as { registration_open_at: string | null }).registration_open_at ?? null,
      opens_at: focus.opens_at ?? null,
      closes_at: focus.closes_at ?? null,
      status: focus.status,
    });
  }, [focus]);

  // Coravu RM programs render a simplified read-only Status surface here
  // instead of the Licensing dashboard (recruiter-side stats, top
  // candidates, recent submissions, timeline) — Coravu owns the case
  // design / deliverables / judging for RM, so the dashboard pivots to
  // an "is your program healthy?" view + an account-manager CTA.
  if (!loading && focus && focus.engagement_model === "rm") {
    return <RmStatusBody row={focus} />;
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: 1200 }}>
        {loading ? (
          <DashboardSkeleton />
        ) : !focus ? (
          <NoCompetitionsState onCreate={() => router.push("/client/competition/new" as Href)} />
        ) : (
          <>
            {/* Page header — the Announce button is provided globally by
                OverviewShell's topbar, so we don't need a per-page button
                here. */}
            <View style={{ marginBottom: 24 }}>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 9.5,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: AMBER,
                  marginBottom: 6,
                }}
              >
                Client Portal
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.displayLight,
                  fontSize: 30,
                  lineHeight: 33,
                  color: W,
                }}
                numberOfLines={2}
              >
                {focus.name}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.bodyLight,
                  fontSize: 13,
                  color: WM,
                  marginTop: 6,
                }}
                numberOfLines={1}
              >
                {focus.company_name ?? "Client"} · {caseStatusLabel(focus.status, days)}
              </Text>

              {err ? (
                <Text style={{ marginTop: 10, fontSize: 12, color: RED, fontFamily: FONTS.body }}>
                  {err}
                </Text>
              ) : null}
            </View>

            {/* Stat row */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12, marginBottom: 26 }}>
              <StatTile value={focusCount.registered} label="Registered Students" delta={null} />
              <StatTile value={focusCount.teams} label="Teams Formed" delta={null} />
              <StatTile value={focusCount.submitted} label="Deliverables Submitted" delta={null} />
              <StatTile
                value={days ?? "—"}
                label="Days Remaining"
                delta={days != null && days <= 7 ? "Deadline approaching" : null}
                amber
              />
            </View>

            {/* Two-column body */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
              <View style={{ flex: 1, minWidth: 360 }}>
                {/* Top Candidates */}
                <DashCard
                  title="Top Candidates — Live Feed"
                  trailing={
                    <PrimaryLink
                      label="View All"
                      onPress={() => router.push(`/client/competition/${focus.id}/candidates` as Href)}
                    />
                  }
                >
                  {candidates.length === 0 ? (
                    <EmptyRow text="No candidates registered yet." />
                  ) : (
                    candidates.map((c) => (
                      <CandidateRow
                        key={c.student_id}
                        candidate={c}
                        onPress={() => router.push(`/client/student/${c.student_id}` as Href)}
                      />
                    ))
                  )}
                  {totals.reg > candidates.length ? (
                    <Pressable
                      onPress={() => router.push(`/client/competition/${focus.id}/candidates` as Href)}
                      style={[
                        {
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                          paddingHorizontal: 20,
                          paddingVertical: 12,
                          opacity: 0.6,
                        },
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <View
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 16,
                          borderWidth: 1,
                          borderColor: BORDER_A,
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(181,135,58,0.06)",
                        }}
                      >
                        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: AMBER }}>
                          +{totals.reg - candidates.length}
                        </Text>
                      </View>
                      <View>
                        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: W }}>
                          {totals.reg - candidates.length} more candidates registered
                        </Text>
                        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL, marginTop: 2 }}>
                          View full roster →
                        </Text>
                      </View>
                    </Pressable>
                  ) : null}
                </DashCard>

                {/* Recent Submissions */}
                <DashCard
                  title="Recent Submissions"
                  trailing={
                    <PrimaryLink
                      label="View All"
                      onPress={() => router.push(`/client/competition/${focus.id}/deliverables` as Href)}
                    />
                  }
                >
                  {submissions.length === 0 ? (
                    <EmptyRow text="No submissions yet — they'll surface here as teams upload." />
                  ) : (
                    submissions.map((s) => (
                      <SubmissionRow
                        key={s.submission_id}
                        submission={s}
                        onPress={() => router.push(`/client/competition/${focus.id}/deliverables` as Href)}
                      />
                    ))
                  )}
                </DashCard>
              </View>

              <View style={{ flex: 1, minWidth: 320 }}>
                <DashCard title="Program Timeline">
                  <View style={{ paddingHorizontal: 20, paddingVertical: 6 }}>
                    {timeline.map((t, i) => (
                      <TimelineRow key={t.key} point={t} last={i === timeline.length - 1} />
                    ))}
                  </View>
                </DashCard>

                <DashCard
                  title="Shortlist"
                  trailing={
                    <PrimaryLink
                      label="Manage"
                      onPress={() => router.push(`/client/competition/${focus.id}/shortlist` as Href)}
                    />
                  }
                >
                  <View style={{ padding: 20, gap: 8 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        color: WM,
                        lineHeight: 18,
                      }}
                    >
                      Mark candidates as shortlisted from the case workspace and they'll appear here for quick
                      access. We'll surface count and the most recent picks once you've started.
                    </Text>
                    <Pressable
                      onPress={() => router.push(`/client/competition/${focus.id}/shortlist` as Href)}
                      style={[
                        {
                          marginTop: 4,
                          alignSelf: "flex-start",
                          paddingVertical: 4,
                        },
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 10.5,
                          letterSpacing: 1.4,
                          textTransform: "uppercase",
                          color: AMBER,
                        }}
                      >
                        Open shortlist →
                      </Text>
                    </Pressable>
                  </View>
                </DashCard>
              </View>
            </View>

            {/* All competitions strip */}
            {rows.length > 1 ? (
              <View style={{ marginTop: 28 }}>
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 9.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: AMBER,
                    marginBottom: 10,
                  }}
                >
                  All Competitions ({rows.length})
                </Text>
                <View style={{ gap: 8 }}>
                  {rows
                    .filter((r) => r.id !== focus.id)
                    .map((r) => (
                      <Pressable
                        key={r.id}
                        onPress={() => router.push(`/client/competition/${r.id}` as Href)}
                        style={[
                          {
                            paddingHorizontal: 18,
                            paddingVertical: 14,
                            backgroundColor: OFF_BLACK,
                            borderWidth: 1,
                            borderColor: BORDER,
                            borderRadius: 4,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          } as ViewStyle,
                          Platform.OS === "web"
                            ? ({ cursor: "pointer", transition: "border-color 180ms" } as object)
                            : null,
                        ]}
                      >
                        <View style={{ minWidth: 0, flex: 1 }}>
                          <Text
                            style={{
                              fontFamily: FONTS.displayMedium,
                              fontSize: 16,
                              color: W,
                            }}
                            numberOfLines={1}
                          >
                            {r.name}
                          </Text>
                          <Text
                            style={{
                              fontFamily: FONTS.body,
                              fontSize: 11.5,
                              color: WL,
                              marginTop: 2,
                            }}
                            numberOfLines={1}
                          >
                            {r.company_name ?? ""} · {r.status} ·{" "}
                            {r.registration_count ?? 0} registered
                          </Text>
                        </View>
                        <Text style={{ color: WL, fontSize: 14, marginLeft: 12 }}>›</Text>
                      </Pressable>
                    ))}
                </View>
              </View>
            ) : null}

            {/* New competition footer button */}
            <View style={{ marginTop: 28 }}>
              <Pressable
                onPress={() => router.push("/client/competition/new" as Href)}
                style={[
                  {
                    alignSelf: "flex-start",
                    paddingHorizontal: 18,
                    paddingVertical: 11,
                    backgroundColor: AMBER,
                    borderRadius: 3,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                    color: FOREST,
                  }}
                >
                  New competition
                </Text>
              </Pressable>
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
}

function NoCompetitionsState({ onCreate }: { onCreate: () => void }) {
  return (
    <View style={{ paddingVertical: 24, maxWidth: 720 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 6,
        }}
      >
        Get started
      </Text>
      <Text
        style={{
          fontFamily: FONTS.displayLight,
          fontSize: 36,
          lineHeight: 40,
          color: W,
          marginBottom: 12,
        }}
      >
        Post your first <Text style={{ fontStyle: "italic", color: AMBER }}>case</Text>.
      </Text>
      <Text
        style={{
          fontFamily: FONTS.bodyLight,
          fontSize: 14,
          lineHeight: 22,
          color: WM,
          maxWidth: 560,
          marginBottom: 22,
        }}
      >
        A case is a real problem you'd hand to a team of analysts in their first week. Coravu opens it to
        students from the schools you specify, gives them a deadline, and surfaces the best work directly to
        you. Most clients are live in under thirty minutes.
      </Text>
      <Pressable
        onPress={onCreate}
        style={[
          {
            alignSelf: "flex-start",
            paddingHorizontal: 22,
            paddingVertical: 13,
            backgroundColor: AMBER,
            borderRadius: 3,
          } as ViewStyle,
          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 12,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: FOREST,
          }}
        >
          Create your first case →
        </Text>
      </Pressable>
    </View>
  );
}

function StatTile({
  value,
  label,
  delta,
  amber = false,
}: {
  value: number | string;
  label: string;
  delta: string | null;
  amber?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const isWebHover = Platform.OS === "web" && hover;
  return (
    <View
      {...(Platform.OS === "web"
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>)
        : {})}
      style={[
        {
          flex: 1,
          minWidth: 200,
          backgroundColor: OFF_BLACK,
          borderWidth: 1,
          borderColor: isWebHover ? BORDER_A : BORDER,
          borderRadius: 4,
          paddingHorizontal: 18,
          paddingVertical: 16,
        } as ViewStyle,
        Platform.OS === "web" ? ({ transition: "border-color 200ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.displayLight,
          fontSize: 36,
          lineHeight: 36,
          color: amber ? AMBER : W,
        }}
      >
        {value}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 11,
          color: WL,
          letterSpacing: 0.4,
          marginTop: 6,
        }}
      >
        {label}
      </Text>
      {delta ? (
        <Text
          style={{
            fontFamily: FONTS.bodySemiBold,
            fontSize: 9.5,
            color: amber ? AMBER : GREEN,
            marginTop: 4,
          }}
        >
          {delta}
        </Text>
      ) : null}
    </View>
  );
}

function DashCard({
  title,
  trailing,
  children,
}: {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <View
      style={{
        backgroundColor: OFF_BLACK,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 4,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: BORDER,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: WL,
          }}
        >
          {title}
        </Text>
        {trailing}
      </View>
      {children}
    </View>
  );
}

function PrimaryLink({ label, onPress }: { label: string; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>)
        : {})}
      style={[
        {
          paddingHorizontal: 11,
          paddingVertical: 5,
          backgroundColor: hover ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.06)",
          borderWidth: 1,
          borderColor: hover ? "rgba(255,255,255,0.15)" : BORDER,
          borderRadius: 2,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer", transition: "all 150ms" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: W,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function CandidateRow({
  candidate,
  onPress,
}: {
  candidate: RecentCandidateRow;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 20,
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: BORDER,
          backgroundColor: hover ? "rgba(181,135,58,0.04)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: "pointer", transition: "background-color 120ms" } as object)
          : null,
      ]}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: "rgba(181,135,58,0.12)",
          borderWidth: 1,
          borderColor: BORDER_A,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 14, color: AMBER }}>
          {candidate.initials}
        </Text>
      </View>
      <View style={{ minWidth: 0, flex: 1 }}>
        <Text
          style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: W, marginBottom: 1 }}
          numberOfLines={1}
        >
          {candidate.full_name ?? "Pending profile"}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL }} numberOfLines={1}>
          {[candidate.major, candidate.university, candidate.team_name ?? "Solo"]
            .filter(Boolean)
            .join(" · ")}
          {candidate.submitted_count > 0
            ? ` · ${candidate.submitted_count} file${candidate.submitted_count === 1 ? "" : "s"} submitted`
            : ""}
        </Text>
      </View>
      <View style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
        <Badge tone={candidate.status === "submitted" ? "green" : "amber"}>
          {candidate.status === "submitted" ? "Submitted" : "In Progress"}
        </Badge>
      </View>
    </Pressable>
  );
}

function SubmissionRow({
  submission,
  onPress,
}: {
  submission: RecentSubmissionRow;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 14,
          paddingHorizontal: 20,
          paddingVertical: 14,
          borderBottomWidth: 1,
          borderBottomColor: BORDER,
          backgroundColor: hover ? "rgba(181,135,58,0.04)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: "pointer", transition: "background-color 120ms" } as object)
          : null,
      ]}
    >
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 4,
          backgroundColor: "rgba(181,135,58,0.10)",
          borderWidth: 1,
          borderColor: BORDER_A,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text style={{ fontSize: 16 }}>{fileEmojiFor(submission.file_name)}</Text>
      </View>
      <View style={{ minWidth: 0, flex: 1 }}>
        <Text
          style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: W, marginBottom: 2 }}
          numberOfLines={1}
        >
          {submission.file_name}
        </Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL }} numberOfLines={1}>
          {[
            submission.deliverable_name,
            submission.team_name,
            timeAgo(submission.submitted_at),
            formatBytes(submission.size_bytes),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Text>
      </View>
      <View style={{ alignItems: "center" }}>
        <Badge tone={submission.status === "late" ? "red" : "green"}>
          {submission.status === "late" ? "Late" : "New"}
        </Badge>
      </View>
    </Pressable>
  );
}

function TimelineRow({
  point,
  last,
}: {
  point: ProgramTimelinePoint;
  last: boolean;
}) {
  const dotColor =
    point.state === "current"
      ? AMBER
      : point.state === "done"
        ? GREEN
        : "transparent";
  const dotBorder = point.state === "upcoming" ? "rgba(255,255,255,0.20)" : dotColor;
  const labelColor =
    point.state === "current" ? AMBER : point.state === "done" ? W : WM;
  const labelWeight =
    point.state === "current"
      ? FONTS.bodySemiBold
      : point.state === "done"
        ? FONTS.bodySemiBold
        : FONTS.bodyMedium;

  return (
    <View style={{ flexDirection: "row", gap: 14, paddingVertical: 14 }}>
      <View style={{ flexDirection: "column", alignItems: "center" }}>
        <View
          style={{
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: dotColor,
            borderWidth: point.state === "upcoming" ? 1 : 0,
            borderColor: dotBorder,
            marginTop: 3,
            ...(point.state === "current"
              ? ({
                  shadowColor: AMBER,
                  shadowOpacity: 0.4,
                  shadowRadius: 6,
                  shadowOffset: { width: 0, height: 0 },
                  ...(Platform.OS === "web"
                    ? { boxShadow: "0 0 0 3px rgba(181,135,58,0.15)" }
                    : {}),
                } as ViewStyle)
              : {}),
          }}
        />
        {!last ? (
          <View
            style={{
              width: 1,
              flex: 1,
              backgroundColor: "rgba(255,255,255,0.07)",
              marginTop: 4,
            }}
          />
        ) : null}
      </View>
      <View style={{ paddingBottom: last ? 0 : 4, flex: 1 }}>
        <Text style={{ fontFamily: labelWeight, fontSize: 13, color: labelColor }}>{point.label}</Text>
        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL, marginTop: 2 }}>
          {point.dateText}
        </Text>
      </View>
    </View>
  );
}

function Badge({ tone, children }: { tone: "green" | "amber" | "red" | "grey"; children: React.ReactNode }) {
  const map = {
    green: { bg: "rgba(127,203,122,0.10)", color: GREEN, border: "rgba(127,203,122,0.22)" },
    amber: { bg: "rgba(181,135,58,0.10)", color: AMBER, border: BORDER_A },
    red: { bg: "rgba(224,90,78,0.10)", color: RED, border: "rgba(224,90,78,0.20)" },
    grey: { bg: "rgba(255,255,255,0.05)", color: WL, border: BORDER },
  } as const;
  const m = map[tone];
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: m.bg,
        borderWidth: 1,
        borderColor: m.border,
        borderRadius: 20,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: m.color,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <View style={{ paddingHorizontal: 20, paddingVertical: 18 }}>
      <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WL }}>{text}</Text>
    </View>
  );
}

function DashboardSkeleton() {
  return (
    <View>
      <Skeleton width={120} height={10} radius={2} />
      <Skeleton
        width={"60%" as unknown as number}
        height={28}
        radius={3}
        style={{ marginTop: 14, marginBottom: 8 }}
      />
      <Skeleton width={"40%" as unknown as number} height={12} radius={2} />

      <View style={{ flexDirection: "row", gap: 12, marginTop: 26 }}>
        {[0, 1, 2, 3].map((k) => (
          <View
            key={k}
            style={{
              flex: 1,
              minWidth: 200,
              backgroundColor: OFF_BLACK,
              borderRadius: 4,
              padding: 18,
              borderWidth: 1,
              borderColor: BORDER,
            }}
          >
            <Skeleton width={70} height={28} />
            <Skeleton width={120} height={10} style={{ marginTop: 14 }} />
          </View>
        ))}
      </View>

      <View style={{ flexDirection: "row", gap: 24, marginTop: 24, flexWrap: "wrap" }}>
        <View style={{ flex: 2, minWidth: 320 }}>
          <Skeleton
            width={"100%" as unknown as number}
            height={120}
            radius={4}
            style={{ marginTop: 12 }}
          />
          <Skeleton
            width={"100%" as unknown as number}
            height={120}
            radius={4}
            style={{ marginTop: 12 }}
          />
        </View>
        <View style={{ flex: 1, minWidth: 260 }}>
          <Skeleton
            width={"100%" as unknown as number}
            height={200}
            radius={4}
            style={{ marginTop: 12 }}
          />
        </View>
      </View>
    </View>
  );
}
